/**
 * ספריית קבצים — the vocabulary of the cross-project file library.
 *
 * The library is a **flat view of `PlanningFileVersions`**: one row per revision that was ever
 * uploaded, not one row per file. That is what "כל הקבצים שעלו בכרטיסי המוצר הפנימיים" asks for,
 * and it is the only shape in which the screen's own "קובץ סופי / הכל" filter means anything — a
 * list of current revisions would have nothing to filter.
 *
 * **"סופי" is `PlanningFileVersion.isCurrent`** and not a new column. The upload drawer already
 * labels that flag "קובץ סופי" (`UploadPlanningFileBody.isCurrent`), a partial unique index already
 * makes at most one current per group a database fact, and a second flag beside it would be a
 * fourth answer to "which revision counts" — the exact thing `planningFileVersion.model.ts` says
 * the schema is arranged to avoid.
 *
 * No React, no MUI, no browser globals: the backend imports this file directly through the
 * `@malash-office/types/product-planning/file-library` alias.
 */

import { PhOption, phOptions } from './productHub.core';
import type { PlanningFileCategory } from './productPlanning.core';

/* ── file type — the coloured extension badge ─────────────────────────────────────────────── */

/**
 * The "סוג קובץ" filter, keyed by **extension** and not by MIME type.
 *
 * The badge in every row renders the extension (`PDF`, `AI`, `DOCX`), the filter has to agree with
 * what the badge shows, and MIME cannot deliver that: Illustrator files arrive as
 * `application/postscript`, `application/illustrator` or `application/octet-stream` depending on
 * the browser that uploaded them, so grouping by MIME would scatter one badge across three filter
 * values. The extension is carried in `DocumentAssets.fileName`, which every revision has.
 *
 * A closed list, because `docs/ai/agent-chat-routes.md` requires it: left open, the agent's
 * `fileType=illustrator` would answer 200 with an empty page instead of 400, and report that the
 * library holds no Illustrator files while it holds forty.
 */
export const PLANNING_FILE_EXTENSIONS = [
  'pdf',
  'ai',
  'eps',
  'psd',
  'jpg',
  'png',
  'gif',
  'svg',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'zip',
  'other'
] as const;
export type PlanningFileExtension = (typeof PLANNING_FILE_EXTENSIONS)[number];

/** Uppercase, because that is how the badge and the filter both read it. `other` is the only word. */
export const PLANNING_FILE_EXTENSION_LABELS: Record<PlanningFileExtension, string> = {
  pdf: 'PDF',
  ai: 'AI',
  eps: 'EPS',
  psd: 'PSD',
  jpg: 'JPG',
  png: 'PNG',
  gif: 'GIF',
  svg: 'SVG',
  doc: 'DOC',
  docx: 'DOCX',
  xls: 'XLS',
  xlsx: 'XLSX',
  ppt: 'PPT',
  pptx: 'PPTX',
  txt: 'TXT',
  csv: 'CSV',
  zip: 'ZIP',
  other: 'אחר'
};

export const PLANNING_FILE_EXTENSION_OPTIONS = phOptions(
  PLANNING_FILE_EXTENSIONS,
  PLANNING_FILE_EXTENSION_LABELS
);

/**
 * Spellings that mean an extension already in the list.
 *
 * `.jpeg` and `.jpg` are one badge and one filter value. Without this the same picture lands under
 * two filter entries depending on which program exported it. Anything genuinely unlisted needs no
 * entry here — it falls to `other` on its own.
 */
const EXTENSION_ALIASES: Record<string, Exclude<PlanningFileExtension, 'other'>> = {
  jpeg: 'jpg',
  jpe: 'jpg'
};

/**
 * The file-name endings each filter value has to match, aliases included.
 *
 * The server filters by extension in SQL (`fileName ILIKE '%.jpg'`), and selecting `JPG` has to
 * return the `.jpeg` files too — otherwise the badge and the filter disagree about the same row.
 * Derived from the alias map rather than written twice, so a new alias reaches both.
 *
 * `other` is absent: it is not a set of endings but the complement of all of them, which only the
 * query that uses this can express.
 */
export const PLANNING_FILE_EXTENSION_SUFFIXES: Record<Exclude<PlanningFileExtension, 'other'>, string[]> =
  PLANNING_FILE_EXTENSIONS.filter((ext): ext is Exclude<PlanningFileExtension, 'other'> => ext !== 'other').reduce(
    (map, ext) => {
      const aliases = Object.keys(EXTENSION_ALIASES).filter(alias => EXTENSION_ALIASES[alias] === ext);
      map[ext] = [ext, ...aliases];
      return map;
    },
    {} as Record<Exclude<PlanningFileExtension, 'other'>, string[]>
  );

/**
 * Every ending that maps to a *named* filter value — so `other` is "none of these".
 *
 * Built with `reduce` and not `Array.flat`: this library is compiled for the backend too, under an
 * older `lib` target where `flat` does not exist.
 */
export const PLANNING_KNOWN_FILE_SUFFIXES: string[] = Object.values(PLANNING_FILE_EXTENSION_SUFFIXES).reduce(
  (all: string[], suffixes) => all.concat(suffixes),
  []
);

/**
 * The extension a file name falls under, for the badge and for the filter.
 *
 * Anything unrecognised is `other` rather than its own value: the filter is a closed list, and a
 * badge reading `DWG` on a row that no filter value can reach is worse than one reading `אחר`.
 */
export const planningFileExtension = (fileName: string | null | undefined): PlanningFileExtension => {
  const name = String(fileName ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return 'other';

  const raw = name.slice(dot + 1).toLowerCase();
  const alias = EXTENSION_ALIASES[raw];
  if (alias) return alias;

  return (PLANNING_FILE_EXTENSIONS as readonly string[]).includes(raw)
    ? (raw as PlanningFileExtension)
    : 'other';
};

/* ── final / draft ────────────────────────────────────────────────────────────────────────── */

/**
 * The screen's "סטטוס קובץ" filter. `final` is `isCurrent`, `draft` is every other revision.
 *
 * Two values and not a boolean, because the filter is a multi-select like its eight neighbours and
 * "both" is expressed by selecting neither — a boolean would need a third value for "הכל".
 */
export const PLANNING_FILE_STATUSES = ['final', 'draft'] as const;
export type PlanningFileStatus = (typeof PLANNING_FILE_STATUSES)[number];

export const PLANNING_FILE_STATUS_LABELS: Record<PlanningFileStatus, string> = {
  final: 'קובץ סופי',
  draft: 'טרם סופי'
};

export const PLANNING_FILE_STATUS_OPTIONS = phOptions(PLANNING_FILE_STATUSES, PLANNING_FILE_STATUS_LABELS);

/* ── view ─────────────────────────────────────────────────────────────────────────────────── */

/** The two tabs beside the search box: the flat table, or the same rows grouped by project. */
export const PLANNING_FILE_VIEWS = ['table', 'grouped'] as const;
export type PlanningFileView = (typeof PLANNING_FILE_VIEWS)[number];

export const PLANNING_FILE_VIEW_LABELS: Record<PlanningFileView, string> = {
  table: 'טבלה',
  grouped: 'לפי מוצר'
};

export const PLANNING_FILE_VIEW_OPTIONS = phOptions(PLANNING_FILE_VIEWS, PLANNING_FILE_VIEW_LABELS);

/* ── the row ──────────────────────────────────────────────────────────────────────────────── */

/**
 * One revision, flattened with everything the table shows about the project it came from.
 *
 * Flattened on purpose: the table has a column per fact and the grouped view keys on
 * `projectId`, so nesting the project would buy nothing and cost every cell a null check.
 */
export interface PlanningFileLibraryRow {
  /** `PlanningFileVersion.id` — the row's identity, and what the drawer is keyed by. */
  id: number;
  groupId: number;
  fileName: string;
  fileUrl: string;
  fileSize: number | null;
  mimeType: string | null;
  /** Derived from `fileName` by `planningFileExtension` — the badge, and the "סוג קובץ" filter. */
  extension: PlanningFileExtension;
  /** The group's name ("Artwork"), which is not the file name ("Artwork REV 5.pdf"). */
  groupName: string;
  category: PlanningFileCategory;
  revision: number;
  /** `isCurrent` — the screen's "קובץ סופי". */
  isFinal: boolean;
  notes: string | null;
  uploadedAt: string;
  uploadedByName: string | null;

  projectId: number;
  projectName: string;
  projectCode: string;
  projectTypeName: string | null;
  projectCategoryName: string | null;
  customerName: string | null;

  /** The kit component the file hangs off, or null when it belongs to the kit as a whole. */
  itemId: number | null;
  itemName: string | null;
  /** The component's מק״ט, falling back to the project's when the file is not tied to a component. */
  sku: string | null;

  /** Every revision of this row's group, newest first — the drawer's "היסטוריית גרסאות". */
  revisions: PlanningFileRevisionRef[];
}

export interface PlanningFileRevisionRef {
  id: number;
  revision: number;
  isFinal: boolean;
}

/* ── query ────────────────────────────────────────────────────────────────────────────────── */

/** Every filter is comma-joined, because every one of them is a `multiSelect` on the screen. */
export interface PlanningFileLibraryParams {
  page?: string | number;
  rowsPerPage?: string | number;
  sortField?: string;
  sortOrder?: string;
  /** File name, project name, project code, item name, or either מק״ט. */
  searchTerm?: string;
  customerId?: string;
  projectTypeId?: string;
  projectCategoryId?: string;
  fileCategory?: string;
  fileType?: string;
  fileStatus?: string;
  itemName?: string;
  uploadedByUserId?: string;
  /** `YYYY-MM-DD`, inclusive on both ends — when the revision was uploaded. */
  uploadedFrom?: string;
  uploadedTo?: string;
}

/** The columns the library may be ordered by. `uploadedAt` is the default, newest first. */
export const PLANNING_FILE_SORT_FIELDS = ['uploadedAt', 'fileName', 'projectName'] as const;
export type PlanningFileSortField = (typeof PLANNING_FILE_SORT_FIELDS)[number];

export interface PlanningFileLibraryResponse {
  rows: PlanningFileLibraryRow[];
  /** Rows matching the filters — what the pager counts. */
  count: number;
  /** Rows before any filter, for the "X מתוך Y" label. */
  totalCount: number;
  /** How many of the matching rows are final — the "N מסומנים כסופיים" note. */
  finalCount: number;
}

/* ── facets ───────────────────────────────────────────────────────────────────────────────── */

/**
 * The option lists the filter panel needs, in one request.
 *
 * Five of the nine dropdowns list values that only a query over the library itself can produce —
 * the kit components that actually carry files, the people who actually uploaded any, the
 * extensions actually present and how many of each. Reading them from the five separate lookup
 * endpoints instead would offer filters that match nothing: every project type in the system,
 * whether or not a single file hangs off it.
 */
export interface PlanningFileLibraryFacets {
  customers: PhOption<string>[];
  projectTypes: PhOption<string>[];
  projectCategories: PhOption<string>[];
  fileCategories: PhOption<string>[];
  /** Labelled with their counts — `PDF (12)` — as the design shows them. */
  fileTypes: PhOption<string>[];
  itemNames: PhOption<string>[];
  uploaders: PhOption<string>[];
}
